import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInterview } from '../../utils/interviewContext';
import { trackPostHogEvent } from '../../utils/posthogUtils';
import { getInterviewFlow, nextStep, previousStep, stepPosition } from '../../utils/interviewFlow';
import styles from './interviewQuestion.module.css';
import { Collapse } from '@mui/material';
import WeekendIcon from '@mui/icons-material/Weekend';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import ProgressBar from '../.shared/ProgressBar/ProgressBar';
import Footer from '../.shared/footer/footer';
import Header from '../.shared/header/header';

const PATH = '/interview/movement';

const movementOptions = [
    { value: 'sedentary', label: 'Mostly sitting — little or no exercise', Icon: WeekendIcon },
    { value: 'light', label: 'Light activity — some walking most days', Icon: DirectionsWalkIcon },
    { value: 'moderate', label: 'Moderately active — exercise 3-4 days a week', Icon: DirectionsRunIcon },
    { value: 'very_active', label: 'Very active — exercise 5+ days a week, or a physical job', Icon: FitnessCenterIcon }
];

export default function InterviewMovement() {
    const navigate = useNavigate();

    const { updateInterviewData, interviewData } = useInterview();
    const [ selectedMovement, setSelectedMovement ] = useState('none');

    // Read once per mount, so the chrome and the navigation cannot disagree if
    // the stored assignment changes while the page is open.
    const flow = useMemo(getInterviewFlow, []);

    // Populate from localStorage on component mount
    useEffect(() => {
        if (interviewData.dailyMovement) {
            setSelectedMovement(interviewData.dailyMovement);
        }

        // Track when user reaches the movement step
        trackPostHogEvent('interview_step_viewed', {
            interview_step: 'movement',
            step_number: stepPosition(flow, PATH),
            total_steps: flow.steps.length,
            interview_flow: flow.id,
            has_existing_data: !!interviewData.dailyMovement
        });
    }, [interviewData.dailyMovement, flow]);

    const handleConfirm = (recentlyChosen) => {
        if (selectedMovement === 'none' && !recentlyChosen) return;

        const chosenMovement = recentlyChosen ? recentlyChosen : selectedMovement;

        setSelectedMovement(chosenMovement);

        // Timeout to allow button selection animation to complete
        setTimeout(() => {
            // Save to interview context
            updateInterviewData('dailyMovement', chosenMovement);

            // Track movement selection
            trackPostHogEvent('interview_movement_selected', {
                selected_movement: chosenMovement,
                movement_label: movementOptions.find((option) => option.value === chosenMovement)?.label,
                interview_step: 'movement',
                step_number: stepPosition(flow, PATH),
                interview_flow: flow.id
            });

            navigate(nextStep(flow, PATH) ?? '/interview/glp1-history');
        }, 300);
    };

    const handleBack = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        navigate(previousStep(flow, PATH) ?? '/');
    };

    return (
        <>
            <div className={styles.interviewPageContainer}>
                <Header hideNavigation={flow.hideNavigation} />
                <ProgressBar step={stepPosition(flow, PATH)} totalSteps={flow.steps.length} />
                <Collapse
                    in
                    appear
                    timeout={500}
                    unmountOnExit
                    mountOnEnter
                    orientation="vertical"
                >
                    <div className={styles.questionContent}>
                        <h1 className={styles.title}>How much movement do you get on a typical day?</h1>
                        <div className={styles.buttonsContainer}>

                            {movementOptions.map(({ value, label, Icon }) => (
                                <button
                                    key={value}
                                    className={`${styles.interviewButton} ${selectedMovement === value ? styles.selected : ''}`}
                                    onClick={() => handleConfirm(value)}
                                >
                                    <Icon sx={{ color: `${selectedMovement === value ? '#0D79FE' : '#4E4E4EFD'}` }} />
                                    <span>{label}</span>
                                </button>
                            ))}

                            <div className={styles.confirmationButtons}>
                                <button
                                    className={styles.secondaryButton}
                                    onClick={handleBack}
                                >
                                    Back
                                </button>
                                <button
                                    className={`${styles.primaryButton} ${selectedMovement === 'none' ? styles.disabledButton : ''}`}
                                    onClick={() => handleConfirm()}
                                >
                                    Continue
                                </button>
                            </div>

                        </div>
                    </div>
                </Collapse>
            </div>
            <Footer />
        </>
    );
}
